import { ExternalLinkIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Button,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  useToast,
} from "@chakra-ui/react";
import { Row, createColumnHelper } from "@tanstack/react-table";
import React from "react";
import { Messages } from "../../../../core/api";
import AgencyMemberSelect from "../../../../shared/components/AgencyMemberSelect";
import AgencyTeamSelect from "../../../../shared/components/AgencyTeamSelect";
import ContractTypeSelect from "../../../../shared/components/ContractTypeSelect";
import DataTable from "../../../../shared/components/DataTable/DataTable";
import useEndpointDataTable, {
  EndpointPageRequest,
} from "../../../../shared/components/DataTable/useEndpointDataTable";
import { toIdValue, usePatientsDashboardAiCapabilities } from "./patientsDashboard.ai";
import DebouncedInput from "../../../../shared/components/DebouncedInput";
import Link from "../../../../shared/components/Link";
import OfficesSelect from "../../../../shared/components/OfficeSelect";
import Page from "../../../../shared/components/Page";
import PatientStatusSelect from "../../../../shared/components/PatientStatusSelect";
import useAuthData from "../../../../shared/hooks/useAuthInfo";
import { useEntityLink } from "../../../../shared/hooks/useEntityLink";
import AddIcon from "../../../../shared/icons/AddIcon";
import ExportNotesOutlineIcon from "../../../../shared/icons/ExportNotesOutlineIcon";
import {
  AgencyMemberId,
  AgencyTeamId,
  ContractTypeId,
  OfficeId,
} from "../../../../shared/schema/schema";
import { fmap } from "../../../../shared/utils";
import { capitalize, toArrayOrNull } from "../../../../shared/utils/common";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { getEntityStatusColorScheme } from "../../../../shared/utils/get-entity-status-color-scheme";
import { getFullName } from "../../../../shared/utils/get-full-name";
import { phoneFormatter } from "../../../../shared/utils/phone-formatter";
import EntityNoteDrawerTrigger from "../../../note/components/EntityNoteDrawerTrigger";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../../../shared/hooks/useApi";
import { formatCustomFieldValue } from "../../../custom-field/custom-field.utils";

export default function PatientsDashboardEndpointRoute() {
  const { agencyMember } = useAuthData();
  const { api, queries } = useApi();
  const toast = useToast();

  const customFieldsQuery = useQuery(queries.customField.list({ entity: "PATIENT" }));

  const columns = React.useMemo(() => {
    return createPatientsTableColumn({
      customFields: customFieldsQuery.data?.list.filter((x) => x.active && x.addToSettings) ?? [],
    });
  }, [customFieldsQuery.data?.list]);

  const toQueryParams = React.useCallback(
    (request: Omit<EndpointPageRequest<PatientFilters>, "limit" | "offset">) => {
      const { filters } = request;

      // List params travel as comma-joined strings, which is the convention the
      // agency search endpoints already use.
      const list = (values: readonly (string | number)[] | null) =>
        values !== null && values.length > 0 ? values.join(",") : undefined;

      return {
        search: filters.search ?? undefined,
        status: list(filters.status),
        officeId: list(filters.officeId),
        contractTypeId: list(filters.contractTypeId),
        agencyTeamId: list(filters.agencyTeamId),
        assignedCoordinatorId: list(filters.assignedCoordinatorId),
        // The header row's own filters, each already keyed by the query param the
        // endpoint expects.
        ...request.columnFilters,
        sortField: request.sortField ?? undefined,
        sortOrder: request.sortOrder,
      };
    },
    []
  );

  const { table, query, totalCount, filters, setFilter, metaButtonProps } = useEndpointDataTable({
    queryKey: ["patient", "list"],
    columns: columns,
    enableColumnFilters: true,
    columnVisiblity: {
      // Its own key: this grid's contract columns have different ids from the
      // GraphQL grid's, so sharing one would mean each remembers a column set
      // the other cannot show.
      storage: { key: "patients/dashboard-endpoint", version: 1 },
      forceVisibleColumns: React.useMemo(() => ["id"], []),
      initialSelected: [
        "firstName",
        "lastName",
        "displayId",
        "address",
        "currentOffice",
        "contracts",
        "@medicaidNumbers",
        "@memberIds",
        "status",
        "createdAt",
        "createdBy",
      ],
    },
    initialSorting: [{ id: "createdAt", desc: true }],
    filters: {
      initialState: {
        search: null,
        // The list opens on the patients an agency is actually serving.
        status: ["ACTIVE"],
        officeId: agencyMember.officeIds,
        contractTypeId: null,
        agencyTeamId: null,
        assignedCoordinatorId: null,
      } satisfies PatientFilters as PatientFilters,
    },
    fetchPage: async (request) => {
      const response = await api.get("./patients/list", {
        query: {
          ...toQueryParams(request),
          limit: request.limit,
          offset: request.offset,
        },
      });

      return { items: response.items, totalCount: response.totalCount };
    },
    fetchAllForExport: async (request) => {
      const params = toQueryParams(request);
      const rows: Patient[] = [];

      while (rows.length < EXPORT_MAX_ROWS) {
        const response = await api.get("./patients/list", {
          query: { ...params, limit: EXPORT_PAGE_SIZE, offset: rows.length },
        });

        rows.push(...response.items);

        if (rows.length >= response.totalCount) {
          return rows;
        }

        if (rows.length >= EXPORT_MAX_ROWS) {
          toast({
            status: "warning",
            title: `Exported the first ${EXPORT_MAX_ROWS.toLocaleString()} of ${response.totalCount.toLocaleString()} patients`,
            description: "Narrow the filters to export the rest.",
          });
        }
      }

      return rows;
    },
  });

  // This page tells the omnibar what its own filters and columns are, while it is on screen. The
  // mapping lives here because it is this list's own filter names - the GraphQL list beside it uses
  // different ones for the same four things.
  usePatientsDashboardAiCapabilities({
    table,
    setFilter: {
      search: (value) => setFilter("search", value),
      status: (value) => setFilter("status", [value as Messages["PatientStatus"]]),
      office: (value) => setFilter("officeId", [toIdValue(value) as OfficeId]),
      contract: (value) => setFilter("contractTypeId", [toIdValue(value) as ContractTypeId]),
      team: (value) => setFilter("agencyTeamId", [toIdValue(value) as AgencyTeamId]),
      coordinator: (value) =>
        setFilter("assignedCoordinatorId", [toIdValue(value) as AgencyMemberId]),
    },
  });

  const entityLink = useEntityLink();

  const handleClickRow = (event: React.MouseEvent<HTMLTableRowElement>, row: Row<Patient>) => {
    entityLink.open({ event, entity: { type: "Patient", id: row.original.id } });
  };

  const filterNode = (
    <>
      <InputGroup width="md">
        <DebouncedInput
          debounce={200}
          placeholder="Search by name, admission, address, etc"
          value={filters.search ?? ""}
          onChange={(x) => setFilter("search", x)}
        />
        <InputRightElement>
          <SearchIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
        </InputRightElement>
      </InputGroup>

      <PatientStatusSelect
        multiple={true}
        value={toArrayOrNull(filters.status)}
        onChange={(x) => setFilter("status", x ?? null)}
      />

      <OfficesSelect
        multiple={true}
        value={filters.officeId}
        onChange={(x) => setFilter("officeId", x ?? null)}
      />

      <ContractTypeSelect
        multiple={true}
        value={toArrayOrNull(filters.contractTypeId)}
        onChange={(x) => setFilter("contractTypeId", x ?? null)}
      />

      <AgencyTeamSelect
        multiple={true}
        value={filters.agencyTeamId}
        onChange={(x) => setFilter("agencyTeamId", x ?? null)}
      />

      <AgencyMemberSelect
        filter={(x) =>
          ["Coordinator", "SeniorCoordinator", "HeadOfCoordination"].includes(x.jobTitle)
        }
        label="Coordinators"
        multiple={true}
        value={filters.assignedCoordinatorId}
        onChange={(x) => setFilter("assignedCoordinatorId", x ?? null)}
      />
    </>
  );

  const actionNode = (
    <Menu isLazy>
      <MenuButton aria-label="Add patient" as={Button} colorScheme="blue" leftIcon={<AddIcon />}>
        Add patients
      </MenuButton>
      <MenuList>
        <MenuItem as={Link} params={{ view: "create-single" }} to="app.patients.dashboard_legacy">
          Add a single patient
        </MenuItem>
        <MenuItem as={Link} params={{ view: "create-multiple" }} to="app.patients.dashboard_legacy">
          Add multiple patients
        </MenuItem>
      </MenuList>
    </Menu>
  );

  return (
    <Page isLoading={query.isPending || query.isFetching}>
      <Page.Header>
        <Page.Title>Patients ({query.isPending ? "..." : totalCount})</Page.Title>
      </Page.Header>

      <Page.Content p={0}>
        <DataTable
          actionNode={actionNode}
          filterNode={filterNode}
          isLoading={query.isPending}
          metaButtonProps={metaButtonProps}
          spacing="tight"
          table={table}
          onClickRow={handleClickRow}
        />
      </Page.Content>
    </Page>
  );
}

type Patient = Messages["PatientListItem"];

type PatientFilters = {
  search: string | null;
  status: Messages["PatientStatus"][] | null;
  officeId: OfficeId[] | null;
  contractTypeId: ContractTypeId[] | null;
  agencyTeamId: AgencyTeamId[] | null;
  assignedCoordinatorId: AgencyMemberId[] | null;
};

/**
 * The endpoint serves one page at a time, so an export walks the pages. It stops
 * at the same result window offset paging cannot see past, and says so rather
 * than handing over a file that is quietly missing rows.
 */
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 10_000;

function createPatientsTableColumn(params: { customFields: Messages["CustomField"][] }) {
  const { accessor, display } = createColumnHelper<Patient>();

  const columns = [
    accessor("lastName", {
      header: "Last Name",
      meta: { gqlFilterKey: "lastNameSearch", gqlSortKey: "lastName" },
    }),
    accessor("firstName", {
      header: "First Name",
      meta: { gqlFilterKey: "firstNameSearch", gqlSortKey: "firstName" },
    }),
    accessor("displayId", {
      header: "ID",
      meta: {
        gqlFilterKey: "displayIdSearch",
        gqlSortKey: "displayId",
      },
      cell: (props) => (
        <Button
          as="a"
          color="black"
          href={`${window.location.pathname}?patient=${props.row.original.id}`}
          rightIcon={<ExternalLinkIcon />}
          target="_blank"
          variant="link"
          onClick={(e) => e.stopPropagation()}
        >
          {props.getValue() ?? props.row.original.id}
        </Button>
      ),
    }),
    display({
      id: "_note-action",
      cell: (props) => (
        <EntityNoteDrawerTrigger
          entity={{
            type: "Patient",
            id: props.row.original.id,
            name: getFullName(props.row.original),
          }}
        >
          <Button
            _hover={{ bg: "blue.100" }}
            colorScheme="blue"
            leftIcon={<ExportNotesOutlineIcon h={5} w={5} />}
            variant="ghost"
            onClick={(e) => e.stopPropagation()}
          >
            Notes
          </Button>
        </EntityNoteDrawerTrigger>
      ),
    }),
    accessor("address", {
      header: "Address",
      meta: { gqlFilterKey: "addressSearch", gqlSortKey: "address" },
    }),
    accessor("homePhoneNumber", {
      header: "Home Phone",
      meta: { gqlFilterKey: "homePhoneSearch", gqlSortKey: "homePhoneNumber" },
      cell: (row) => fmap(row.getValue(), phoneFormatter.formatNationalIfValid),
    }),
    accessor("mobilePhoneNumber", {
      header: "Mobile Phone",
      meta: { gqlFilterKey: "mobilePhoneSearch", gqlSortKey: "mobilePhoneNumber" },
      cell: (row) => fmap(row.getValue(), phoneFormatter.formatNationalIfValid),
    }),
    accessor("dateOfBirth", {
      header: "Date of Birth",
      meta: { gqlSortKey: "dateOfBirth" },
      cell: (row) => fmap(row.getValue(), dateFormatter.toDate),
    }),
    accessor((x) => x.currentOffice?.name, {
      id: "currentOffice",
      header: "Office",
      meta: { gqlSortKey: "currentOfficeId" },
    }),
    accessor("contracts", {
      header: "Contracts",
      meta: { csvFn: (x) => x.map((contract) => contract.contractTypeName).join(", ") },
      cell: (row) =>
        row
          .getValue()
          .map((contract) => contract.contractTypeName)
          .join(", "),
    }),
    accessor("contracts", {
      id: "medicaidNumbers",
      header: "Medicaid numbers",
      meta: {
        csvFn: (x) =>
          x
            .map((contract) => contract.medicaidNumber)
            .filter(Boolean)
            .join(", "),
      },
      cell: (row) =>
        row
          .getValue()
          .map((contract) => contract.medicaidNumber)
          .filter(Boolean)
          .join(", "),
    }),
    accessor("contracts", {
      id: "memberIds",
      header: "Member IDs",
      meta: { csvFn: (x) => x.map((contract) => contract.memberId).join(", ") },
      cell: (row) =>
        row
          .getValue()
          .map((contract) => contract.memberId)
          .join(", "),
    }),
    accessor("startOfCare", {
      header: "Start of Care",
      meta: { gqlSortKey: "startOfCare" },
      cell: (row) => fmap(row.getValue(), dateFormatter.toDate),
    }),
    accessor((x) => x.agencyTeam?.name, {
      id: "agencyTeam",
      header: "Team",
      meta: { gqlSortKey: "agencyTeamId" },
    }),
    accessor("assignedCoordinator", {
      header: "Coordinator",
      meta: { gqlSortKey: "assignedCoordinatorId" },
      cell: (row) => fmap(row.getValue(), getFullName),
    }),
    accessor("status", {
      header: "Status",
      meta: { gqlSortKey: "status" },
      cell: (row) => (
        <Tag colorScheme={getEntityStatusColorScheme(row.getValue())}>
          {capitalize(row.getValue())}
        </Tag>
      ),
    }),
    accessor("effectiveDate", {
      header: "Effective Date",
      meta: { gqlSortKey: "effectiveDate" },
      cell: (row) => fmap(row.getValue(), dateFormatter.toDate),
    }),
    accessor("createdAt", {
      header: "Created at",
      meta: { gqlSortKey: "createdAt" },
      cell: (row) => dateFormatter.toDateOrDateTime(row.getValue()),
    }),
    accessor("createdBy", {
      header: "Created by",
      meta: { gqlSortKey: "createdBy" },
      cell: (row) => fmap(row.getValue(), getFullName),
    }),
  ];

  for (const customField of params.customFields) {
    columns.push(
      display({
        id: `customField-${customField.id}`,
        header: customField.fieldName,
        meta: {
          csvFn: (_, x) => fmap(x.customFields[customField.fieldName], formatCustomFieldValue),
        },
        cell: ({ row }) => {
          return fmap(row.original.customFields[customField.fieldName], formatCustomFieldValue);
        },
      })
    );
  }

  return columns;
}
